import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronRight, Loader } from 'lucide-react';
import { getApiUrl } from '../../config/api';

export default function EventPickerPage() {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            setError('');
            const apiUrl = getApiUrl();
            const response = await fetch(`${apiUrl}/events/by-domain`);
            const data = await response.json();

            if (response.ok) {
                setEvents(data.events || []);
            } else {
                setError(data.error || 'Failed to load events');
            }
        } catch (err) {
            setError(`Failed to load events: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const resolveBannerUrl = (url) => {
        if (!url || typeof url !== 'string') return null;
        const u = url.trim();
        if (u.startsWith('http://')) return u.replace('http://', 'https://');
        if (u.startsWith('https://') || u.startsWith('/')) return u;
        return u;
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <Loader className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading events...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center max-w-md mx-auto p-6">
                    <p className="text-red-600 mb-4">{error}</p>
                    <button
                        onClick={fetchEvents}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (events.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center max-w-md mx-auto p-6">
                    <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">No events available</h2>
                    <p className="text-gray-600">There are no events configured for this domain.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-2xl font-bold text-gray-900 mb-8">Choose an event</h1>
                <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2">
                    {events.map((event) => {
                        const bannerUrl = resolveBannerUrl(event.banner_image_url);
                        const key = event.key || event.short_code || event.slug;
                        return (
                            <button
                                key={event.id}
                                onClick={() => navigate(`/${key}`)}
                                className="text-left bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            >
                                <div className="aspect-video bg-gray-200 relative">
                                    {bannerUrl ? (
                                        <img
                                            src={bannerUrl}
                                            alt={event.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Calendar className="w-16 h-16 text-gray-400" />
                                        </div>
                                    )}
                                </div>
                                <div className="p-4 flex items-center justify-between">
                                    <div>
                                        <h3 className="font-semibold text-gray-900">{event.name}</h3>
                                        {event.description && (
                                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                                {event.description}
                                            </p>
                                        )}
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
